// The email sender, Stage 5 piece 1. EMAIL_SENDER_WORDS.md and MOCKUP_EMAIL_SENDER.html (approved 2026-10-10).
//
// How an email goes out:
// 1. Server code adds one document to companies/{company}/emailLog with status "queued"
//    (queuedEmailOf). It can ride in the same transaction as the record it is about.
// 2. The sendQueuedEmail trigger picks it up, tries the main route (alerts@getsafetag.com),
//    then the backup Google account, a few tries each (deliver).
// 3. The same document records the route, every try, and Sent or Failed.
// Nothing in the app waits on an email or stops because one failed.
//
// Dev never emails real people: on any project but prod every email goes to the dev
// address, with a grey line naming who it would reach on prod.
//
// Pure: data in, data out. Shared with the server and unit tested.

import { closedAtText } from "./jsp-close";

export const PROD_PROJECT = "getsafetag-prod";
export const MAIN_FROM = "SafeTag ESP <alerts@getsafetag.com>";
export const SENDER_NAME = "SafeTag ESP";
/** Tries on each route before moving on. */
export const TRIES_PER_ROUTE = 3;
/** Waits between tries on one route, in milliseconds. */
export const RETRY_WAITS_MS = [2000, 5000];
/** The tile counts failed emails this many days back. */
export const FAILED_WINDOW_DAYS = 7;
/** The Email Log reads this many of the newest emails. */
export const LOG_LIMIT = 300;

export interface Recipient {
  name: string;
  email: string;
}

/** What server code hands the sender. Every field is plain text; the sender escapes it. */
export interface EmailRequest {
  /** Two-digit site. Blank for a program-wide email. */
  siteNumber: string;
  siteName: string;
  companyName: string;
  to: Recipient[];
  /** Who they are as a group: "Springfield LOTO verifiers". */
  toLabel: string;
  subject: string;
  /** The one bold line of what to do. */
  headline: string;
  lines: string[];
  rows: { label: string; value: string }[];
  /** Opens the right page in the app. Path is the part after the address: "#/people/p/01-QEW13/loto". */
  button: { label: string; path: string } | null;
  /** Why this person got it. */
  footer: string;
  /** "LOTO verify" */
  reason: string;
  /** The record it belongs to: "01-QEW13". */
  recordId: string;
}

export type EmailStatus = "queued" | "sending" | "sent" | "failed";
export type RouteName = "main" | "backup";

export interface SendTry {
  route: RouteName;
  at: string;
  ok: boolean;
  error: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What is wrong with a request, or null. Server code checks before it queues. */
export function emailRequestProblem(r: EmailRequest): string | null {
  if (r.siteNumber && !/^\d{2}$/.test(r.siteNumber)) return "Site number is two digits.";
  if (!r.to.length) return "Nobody to send to.";
  const bad = r.to.find((x) => !EMAIL.test(x.email.trim()));
  if (bad) return `Not a working email address: ${bad.email}`;
  if (!r.subject.trim()) return "Subject is required.";
  if (!r.headline.trim()) return "Headline is required.";
  if (!r.reason.trim()) return "Reason is required.";
  return null;
}

/** The document server code adds to emailLog. The trigger sends it. */
export function queuedEmailOf(r: EmailRequest, createdAt: string, createdBy: string): Record<string, unknown> {
  const problem = emailRequestProblem(r);
  if (problem) throw new Error(problem);
  return {
    ...r,
    to: r.to.map((x) => ({ name: x.name.trim(), email: x.email.trim().toLowerCase() })),
    status: "queued" as EmailStatus,
    createdAt,
    createdBy,
    route: null,
    sentTo: [],
    devLine: "",
    replyTo: "",
    tries: [],
    error: "",
    sentAt: null,
    html: "",
    text: "",
  };
}

// ---- Who it really goes to ----

export function isProd(project: string): boolean {
  return project === PROD_PROJECT;
}

/** "Sean Terry" for one person. "Springfield LOTO verifiers (2)" for a group. */
export function recipientsLine(r: Pick<EmailRequest, "to" | "toLabel">): string {
  if (r.to.length === 1) return r.to[0]!.name || r.to[0]!.email;
  return `${r.toLabel || "Recipients"} (${r.to.length})`;
}

export interface Delivery {
  sentTo: string[];
  /** The grey dev line. Blank on prod. */
  devLine: string;
  /** Set when nothing can be sent. */
  problem: string;
}

export function deliveryOf(r: Pick<EmailRequest, "to" | "toLabel">, project: string, devTo: string): Delivery {
  if (isProd(project)) return { sentTo: r.to.map((x) => x.email), devLine: "", problem: "" };
  const to = devTo.trim();
  if (!EMAIL.test(to)) return { sentTo: [], devLine: "", problem: "Dev has no test address. Nothing was sent." };
  return { sentTo: [to], devLine: `Dev test. On prod this goes to: ${recipientsLine(r)}.`, problem: "" };
}

/** The permanent address of the live app. Labels use it too. */
export const PROD_DOMAIN = "getsafetag.com";

/** The app's address on this project. Prod uses getsafetag.com (Robert, 2026-10-12). */
export function appUrl(project: string, path: string): string {
  const p = path.replace(/^\/+/, "");
  const host = isProd(project) ? PROD_DOMAIN : `${project}.web.app`;
  return `https://${host}/${p}`;
}

// ---- The email itself ----

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** "Alltech · Springfield, KY" */
function placeLine(r: EmailRequest): string {
  return [r.companyName, r.siteName].filter(Boolean).join(" · ");
}

/** Plain-text copy for mail apps that block formatting. */
export function emailText(r: EmailRequest, devLine: string, project: string): string {
  const out: string[] = [];
  if (devLine) out.push(devLine, "");
  out.push(`${SENDER_NAME} · ${placeLine(r)}`, "", r.headline, "");
  for (const l of r.lines) out.push(l, "");
  if (r.rows.length) {
    for (const x of r.rows) out.push(`${x.label}: ${x.value}`);
    out.push("");
  }
  if (r.button) out.push(`${r.button.label}: ${appUrl(project, r.button.path)}`, "");
  if (r.footer) out.push(r.footer);
  return out.join("\n").trim() + "\n";
}

/** The branded copy. Tables and inline styles, so it holds together in phone mail apps. */
export function emailHtml(r: EmailRequest, devLine: string, project: string): string {
  const font = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
  const dev = devLine
    ? `<tr><td style="background:#f3f4f6;border-bottom:1px solid #e3e6ea;padding:8px 14px;font-size:12px;color:#5f646b">${esc(devLine)}</td></tr>`
    : "";
  const lines = r.lines.map((l) => `<p style="margin:0 0 10px">${esc(l)}</p>`).join("");
  const rows = r.rows.length
    ? `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border:1px solid #e3e6ea;border-radius:8px;border-collapse:separate;margin:4px 0 12px">${r.rows
        .map(
          (x, i) =>
            `<tr><td style="padding:7px 10px;font-size:13px;color:#5f646b;${i ? "border-top:1px solid #e3e6ea;" : ""}">${esc(x.label)}</td><td align="right" style="padding:7px 10px;font-size:13px;font-weight:700;color:#111;${i ? "border-top:1px solid #e3e6ea;" : ""}">${esc(x.value)}</td></tr>`,
        )
        .join("")}</table>`
    : "";
  const button = r.button
    ? `<table role="presentation" width="100%" cellspacing="0" cellpadding="0"><tr><td align="center" style="background:#F5C400;border:2px solid #111;border-radius:10px"><a href="${esc(appUrl(project, r.button.path))}" style="display:block;padding:12px;color:#111;font-weight:800;font-size:15px;text-decoration:none">${esc(r.button.label)}</a></td></tr></table>`
    : "";
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${esc(r.subject)}</title></head>
<body style="margin:0;padding:0;background:#f0f2f5;font-family:${font};color:#111">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f0f2f5"><tr><td align="center" style="padding:12px">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:560px;background:#ffffff;border:1px solid #e3e6ea;border-radius:10px;border-collapse:separate;overflow:hidden">
${dev}
<tr><td style="background:#111;padding:12px 14px"><table role="presentation" width="100%" cellspacing="0" cellpadding="0"><tr><td style="color:#F5C400;font-weight:800;font-size:15px">${SENDER_NAME}</td><td align="right" style="color:#cfd3d8;font-size:11px;letter-spacing:1px;text-transform:uppercase">${esc(placeLine(r))}</td></tr></table></td></tr>
<tr><td style="padding:14px;font-size:15px;line-height:1.5">
<p style="margin:0 0 10px;font-size:18px;font-weight:800">${esc(r.headline)}</p>
${lines}${rows}${button}
</td></tr>
${r.footer ? `<tr><td style="padding:10px 14px;border-top:1px solid #e3e6ea;font-size:12px;color:#8a8f96;line-height:1.5">${esc(r.footer)}</td></tr>` : ""}
</table></td></tr></table></body></html>`;
}

// ---- Sending: main route, then backup ----

/** A route's refusal. retry false means trying the same route again will not help. */
export class SendError extends Error {
  constructor(
    message: string,
    readonly retry: boolean,
  ) {
    super(message);
  }
}

export interface Outgoing {
  to: string[];
  replyTo: string;
  subject: string;
  html: string;
  text: string;
}

export interface Route {
  name: RouteName;
  /** False when the route has no key yet. It is skipped without a try. */
  ready: boolean;
  send(mail: Outgoing): Promise<void>;
}

export interface DeliverResult {
  status: "sent" | "failed";
  route: RouteName | null;
  tries: SendTry[];
  error: string;
}

/**
 * Main route first, a few tries, then the backup. A refusal that will not change on a
 * retry (a bad key, a bad address) moves straight to the next route.
 */
export async function deliver(
  mail: Outgoing,
  routes: Route[],
  wait: (ms: number) => Promise<void> = (ms) => new Promise((ok) => setTimeout(ok, ms)),
  clock: () => string = () => new Date().toISOString(),
): Promise<DeliverResult> {
  const tries: SendTry[] = [];
  let last = "";
  for (const route of routes) {
    if (!route.ready) continue;
    for (let n = 0; n < TRIES_PER_ROUTE; n++) {
      if (n > 0) await wait(RETRY_WAITS_MS[Math.min(n - 1, RETRY_WAITS_MS.length - 1)]!);
      try {
        await route.send(mail);
        tries.push({ route: route.name, at: clock(), ok: true, error: "" });
        return { status: "sent", route: route.name, tries, error: "" };
      } catch (e) {
        last = e instanceof Error ? e.message : String(e);
        tries.push({ route: route.name, at: clock(), ok: false, error: last });
        if (e instanceof SendError && !e.retry) break;
      }
    }
  }
  if (!tries.length) return { status: "failed", route: null, tries, error: "No email route is set up. Nothing was sent." };
  return { status: "failed", route: null, tries, error: last };
}

/** Resend answers: 429 and 5xx can pass on a retry. Other 4xx will not. */
export function httpRetryable(status: number): boolean {
  return status === 429 || status >= 500;
}

/** SMTP answers: 4xx is "try later", 5xx is final. No code means the line dropped. */
export function smtpRetryable(responseCode: number | undefined): boolean {
  return responseCode === undefined || (responseCode >= 400 && responseCode < 500);
}

// ---- Send Test Email (Robert, 2026-10-10: yes, on the Email Log page) ----
// Two emails, one forced through each route, so each route is proven on its own.
// Program-wide (no site). To the program contact; on dev the sender redirects it to the dev address anyway.

export const TEST_REASON = "Test";

export function testEmailRequest(route: RouteName, companyName: string, to: string, byName: string): EmailRequest {
  const name = route === "main" ? "main route" : "backup route";
  return {
    siteNumber: "",
    siteName: "",
    companyName,
    to: [{ name: "Program contact", email: to }],
    toLabel: "Program contact",
    subject: `SafeTag test email · ${name}`,
    headline: `The ${name} works.`,
    lines: [`${byName} sent this from the Email Log to prove the ${name}. Nothing needs doing.`],
    rows: [{ label: "Route", value: routeLabel(route) }],
    button: { label: "Open Email Log", path: "#/email-log/all" },
    footer: "Test email. Sent by SafeTag for the Electrical Safety Program.",
    reason: TEST_REASON,
    recordId: route === "main" ? "Main route" : "Backup route",
  };
}

/** The trigger's line when a test is forced onto a route that has no key yet. */
export function routeNotReadyLine(route: RouteName): string {
  return route === "main"
    ? "The main route is not set up yet. It needs the getsafetag.com mail service key."
    : "The backup route is not set up yet. It needs the SafeTag Google account and its app password.";
}

// ---- The Email Log page ----

export interface EmailRow {
  emailId: string;
  siteNumber: string;
  subject: string;
  reason: string;
  recordId: string;
  status: EmailStatus;
  route: RouteName | null;
  createdAt: string;
  sentAt: string | null;
  who: string;
  to: Recipient[];
  toLabel: string;
  sentTo: string[];
  devLine: string;
  replyTo: string;
  tries: SendTry[];
  error: string;
  html: string;
  text: string;
}

const STATUSES: EmailStatus[] = ["queued", "sending", "sent", "failed"];

function strOf(v: unknown): string {
  return typeof v === "string" ? v : "";
}

export function emailRowOf(emailId: string, d: Record<string, unknown>): EmailRow {
  const to = Array.isArray(d.to) ? (d.to as unknown[]).map((x) => ({ name: strOf((x as Recipient)?.name), email: strOf((x as Recipient)?.email) })) : [];
  const toLabel = strOf(d.toLabel);
  const tries = Array.isArray(d.tries)
    ? (d.tries as unknown[]).map((x) => {
        const t = (x ?? {}) as Record<string, unknown>;
        return { route: (t.route === "backup" ? "backup" : "main") as RouteName, at: strOf(t.at), ok: t.ok === true, error: strOf(t.error) };
      })
    : [];
  return {
    emailId,
    siteNumber: strOf(d.siteNumber),
    subject: strOf(d.subject),
    reason: strOf(d.reason),
    recordId: strOf(d.recordId),
    status: STATUSES.includes(d.status as EmailStatus) ? (d.status as EmailStatus) : "queued",
    route: d.route === "main" || d.route === "backup" ? d.route : null,
    createdAt: strOf(d.createdAt),
    sentAt: typeof d.sentAt === "string" ? d.sentAt : null,
    who: to.length ? recipientsLine({ to, toLabel }) : "",
    to,
    toLabel,
    sentTo: Array.isArray(d.sentTo) ? (d.sentTo as unknown[]).map(strOf) : [],
    devLine: strOf(d.devLine),
    replyTo: strOf(d.replyTo),
    tries,
    error: strOf(d.error),
    html: strOf(d.html),
    text: strOf(d.text),
  };
}

/** Newest first. A site, or "all". Failed only when asked. */
export function emailRowsFor(rows: EmailRow[], site: string, failedOnly: boolean): EmailRow[] {
  return rows
    .filter((r) => site === "all" || r.siteNumber === site)
    .filter((r) => !failedOnly || r.status === "failed")
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** The amber count on the tile: failed in the last 7 days. */
export function failedThisWeek(rows: EmailRow[], nowIso: string): number {
  const from = new Date(new Date(nowIso).getTime() - FAILED_WINDOW_DAYS * 86_400_000).toISOString();
  return rows.filter((r) => r.status === "failed" && r.createdAt >= from).length;
}

export interface EmailPill {
  label: string;
  tone: "grey" | "amber" | "red";
}

/** Grey Sent is normal. Amber Sent · Backup: the main route was down. Red Failed: nobody got it. No green. */
export function emailPillOf(r: Pick<EmailRow, "status" | "route">): EmailPill {
  if (r.status === "failed") return { label: "FAILED", tone: "red" };
  if (r.status === "sent") return r.route === "backup" ? { label: "SENT · BACKUP", tone: "amber" } : { label: "SENT", tone: "grey" };
  return { label: "SENDING", tone: "grey" };
}

/** "10/10 06:42 · 2 verifiers · LOTO verify, 01-QEW13" */
export function emailRowLine(r: EmailRow): string {
  const why = [r.reason, r.recordId].filter(Boolean).join(", ");
  return [closedAtText(r.createdAt), r.who, why].filter(Boolean).join(" · ");
}

export function routeLabel(route: RouteName | null): string {
  return route === "main" ? "Main · alerts@getsafetag.com" : route === "backup" ? "Backup · SafeTag Google account" : "None";
}

/** The tile's grey line. */
export function emailTileLine(rows: EmailRow[] | null, failed: number): string {
  if (!rows) return "…";
  if (failed) return `${failed} failed this week`;
  return rows.length ? "All sent" : "None yet";
}
